import { type RefObject, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatPrice } from '@witmetrics/utils/strings';
import { getOrderTotals } from '@/utils/pricing';
import { useCheckout } from '@/providers/CheckoutProvider';
import { useShopProduct } from '@/hooks/useShopProduct';
import { useToggle } from '@/hooks/useToggle';
import Button from '@/components/Button';
import { classes } from './utils';

export interface BuyBarProps {
  orderCardRef: RefObject<HTMLDivElement | null>;
}

export default function BuyBar({ orderCardRef }: BuyBarProps) {
  const { quantity } = useCheckout();
  const { currency, unitPrice } = useShopProduct();
  const [isCardInView, toggleCardInView] = useToggle(false);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserve);
    // Observe a frame late: same-frame observe records a pre-layout miss on iOS Safari
    const frame = requestAnimationFrame(() => {
      if (orderCardRef.current) observer.observe(orderCardRef.current);
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const handleObserve = (entries: IntersectionObserverEntry[]) => {
    // Batched records arrive oldest-first; only the newest reflects reality (iOS load)
    toggleCardInView(entries[entries.length - 1].isIntersecting);
  };

  const handleBuyNow = () => {
    orderCardRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const totals = getOrderTotals(quantity, unitPrice);

  return (
    <div className={twMerge(classes.bar, isCardInView && classes.barHidden)}>
      <div>
        <div className={classes.totalLabel}>Total, excl. VAT</div>
        <div className={classes.total}>
          {formatPrice(totals.total, currency)}
        </div>
      </div>
      <Button className={classes.cta} onClick={handleBuyNow}>
        Buy now
      </Button>
    </div>
  );
}
